import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, X, Copy, FileDown } from 'lucide-react';
import { renderField } from './renderField';

// Pure presentational medical/SOAP note card. Receives note + handlers.
export function MedicalNoteCard({ medicalNote, onClose, onCopy, onCopyCode, onExport }) {
  return (
    <Card
      className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 backdrop-blur-xl rounded-3xl border border-purple-200 shadow-xl"
      data-testid="medical-note-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Medical Note</h3>
        </div>
        <Button onClick={onClose} variant="ghost" size="sm" className="rounded-full">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          onClick={() => onCopy(medicalNote.full_note, 'Medical note')}
          size="sm"
          variant="outline"
          data-testid="copy-note-button"
          className="rounded-lg text-xs border-purple-200 text-purple-700 hover:bg-purple-100"
        >
          <Copy className="w-3 h-3 mr-1" />
          Copy
        </Button>
        <Button
          onClick={() => onExport('text')}
          size="sm"
          variant="outline"
          data-testid="export-text-button"
          className="rounded-lg text-xs border-purple-200 text-purple-700 hover:bg-purple-100"
        >
          <FileDown className="w-3 h-3 mr-1" />
          TXT
        </Button>
        <Button
          onClick={() => onExport('fhir')}
          size="sm"
          variant="outline"
          data-testid="export-fhir-button"
          className="rounded-lg text-xs border-purple-200 text-purple-700 hover:bg-purple-100"
        >
          <FileDown className="w-3 h-3 mr-1" />
          FHIR
        </Button>
        <Button
          onClick={() => onExport('ccd')}
          size="sm"
          variant="outline"
          data-testid="export-ccd-button"
          className="rounded-lg text-xs border-purple-200 text-purple-700 hover:bg-purple-100"
        >
          <FileDown className="w-3 h-3 mr-1" />
          CCD
        </Button>
      </div>

      <p className="text-xs text-purple-700 mb-4 bg-white/40 p-2 rounded-lg">
        Export formats compatible with major EHR systems (Epic, Cerner, Allscripts)
      </p>

      {(medicalNote.icd10_codes?.length > 0 || medicalNote.cpt_codes?.length > 0) && (
        <div className="mb-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-700 uppercase mb-2 flex items-center gap-2">
            <span>💳</span> Billing Codes
          </p>

          {medicalNote.icd10_codes?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-600 mb-1">ICD-10 Diagnostic Codes:</p>
              <div className="space-y-1">
                {medicalNote.icd10_codes.map((code, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2 text-xs">
                    <span className="font-mono font-semibold text-green-700">{code.code}</span>
                    <span className="text-gray-700 flex-1 mx-2">{code.description}</span>
                    <button
                      onClick={() => onCopyCode(code.code, 'ICD-10 code')}
                      className="text-green-600 hover:text-green-700"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {medicalNote.cpt_codes?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">CPT Procedure Codes:</p>
              <div className="space-y-1">
                {medicalNote.cpt_codes.map((code, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2 text-xs">
                    <span className="font-mono font-semibold text-blue-700">{code.code}</span>
                    <span className="text-gray-700 flex-1 mx-2">{code.description}</span>
                    <button
                      onClick={() => onCopyCode(code.code, 'CPT code')}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {medicalNote.chief_complaint && medicalNote.chief_complaint !== 'See full note' && (
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Chief Complaint</p>
            <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl">{renderField(medicalNote.chief_complaint)}</p>
          </div>
        )}

        {medicalNote.history_present_illness && medicalNote.history_present_illness !== 'See full note' && (
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">History of Present Illness</p>
            <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl">{renderField(medicalNote.history_present_illness)}</p>
          </div>
        )}

        {medicalNote.assessment && medicalNote.assessment !== 'See full note' && (
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Assessment</p>
            <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl">{renderField(medicalNote.assessment)}</p>
          </div>
        )}

        {medicalNote.plan && medicalNote.plan !== 'See full note' && (
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Treatment Plan</p>
            <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl">{renderField(medicalNote.plan)}</p>
          </div>
        )}

        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Full Note</p>
          <div className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl whitespace-pre-wrap">{renderField(medicalNote.full_note)}</div>
        </div>
      </div>
    </Card>
  );
}
